import React, { useEffect, useState, useRef, useCallback } from "react";
import { Bell, BellRing, Calendar, Clock, Stethoscope, X, AlarmClock, CheckCircle2 } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";

const POLL_MS = 60_000; // refresh every minute

// Tier styling for urgency bands shown on cards.
const URGENCY = {
  yellow: { color: "#FBBF24", bg: "bg-amber-500/10",  border: "border-amber-500/40",  label: "Upcoming" },
  orange: { color: "#FB923C", bg: "bg-orange-500/15", border: "border-orange-500/50", label: "Soon" },
  red:    { color: "#FB7185", bg: "bg-rose-500/15",   border: "border-rose-500/50",   label: "Overdue" },
};

/** Pretty-print "in 2h 30m" / "in 1d 4h" / "3h overdue" */
function relativeWhen(minutes) {
  const overdue = minutes < 0;
  const abs = Math.abs(minutes);
  const d = Math.floor(abs / (60 * 24));
  const h = Math.floor((abs % (60 * 24)) / 60);
  const m = abs % 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (!d && !h) parts.push(`${m}m`);
  const txt = parts.join(" ");
  return overdue ? `${txt} overdue` : `in ${txt}`;
}

/** Format "YYYY-MM-DDTHH:MM" -> "Mon 24 Feb · 09:30" */
function fmtWhen(iso) {
  if (!iso) return "—";
  const [d, t] = iso.split("T");
  return `${fmtDate(d)} · ${(t || "").slice(0, 5)}`;
}

/** Add minutes to current time, return "YYYY-MM-DDTHH:MM" in *local* clock. */
function inMinutes(mins) {
  const d = new Date(Date.now() + mins * 60_000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function NotificationBell() {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Track which records have already triggered a login banner so we don't
  // re-toast on every refresh during the session.
  const toastedRef = useRef(new Set());
  // Banner state for due-today / overdue toast.
  const [banner, setBanner] = useState(null);

  const popoverRef = useRef(null);

  const load = useCallback(async (firstLoad = false) => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const r = await api.get("/appointments/upcoming");
      const list = r.data?.items || [];
      setItems(list);
      // On first load only, surface a banner if there's any red/orange item.
      if (firstLoad) {
        const urgent = list.find((it) => it.urgency === "red" || it.urgency === "orange");
        if (urgent && !toastedRef.current.has(urgent.record_id)) {
          toastedRef.current.add(urgent.record_id);
          const moreCount = list.filter((it) => (it.urgency === "red" || it.urgency === "orange") && it.record_id !== urgent.record_id).length;
          setBanner({ first: urgent, moreCount });
        }
      }
    } catch (err) {
      // Silently surface in DevTools so we can debug intermittent
      // /appointments/upcoming failures — but don't break the navbar chrome.
      console.warn("[notifications] /appointments/upcoming failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // First load + interval refresh.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(id);
  }, [isAuthenticated, load]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Listen for global "appointment changed" events (e.g. after editing a record).
  useEffect(() => {
    const handler = () => load(false);
    window.addEventListener("ych:appointments-changed", handler);
    return () => window.removeEventListener("ych:appointments-changed", handler);
  }, [load]);

  const snooze = async (recordId, until) => {
    await api.post(`/appointments/${recordId}/snooze`, { snooze_until: until });
    await load(false);
  };
  const dismiss = async (recordId) => {
    await api.post(`/appointments/${recordId}/dismiss`);
    await load(false);
  };

  const count = items.length;
  const overdueCount = items.filter((it) => it.urgency === "red").length;
  const soonCount = items.filter((it) => it.urgency === "orange").length;
  const Icon = count > 0 ? BellRing : Bell;
  const badgeColor = overdueCount > 0 ? "#FB7185" : soonCount > 0 ? "#FB923C" : "#FBBF24";

  return (
    <>
      <div className="relative" ref={popoverRef} data-testid="notification-bell-container">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`relative pill ${count > 0 ? "" : ""}`}
          data-testid="notification-bell-btn"
          title={count > 0 ? `${count} appointment reminder${count === 1 ? "" : "s"}` : "No upcoming appointments"}
        >
          <Icon size={15} className={count > 0 ? "text-amber-300" : ""} />
          <span className="hidden sm:inline">Reminders</span>
          {count > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-mono-data font-bold flex items-center justify-center"
              style={{ background: badgeColor, color: "#0B1627" }}
              data-testid="notification-badge"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>

        {open && (
          <div
            className="absolute right-0 mt-2 w-[380px] md:w-[420px] max-h-[70vh] overflow-y-auto rounded-xl border border-[#1E304B] bg-[#0B1627] shadow-2xl z-50"
            data-testid="notification-dropdown"
          >
            <div className="sticky top-0 bg-[#0B1627] border-b border-[#1E304B] px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Appointment reminders</div>
                <div className="text-sm font-display font-semibold text-slate-100 flex items-center gap-2">
                  {count} pending
                  {overdueCount > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-mono-data" style={{ background: "#FB718522", color: "#FB7185", border: "1px solid #FB718555" }}>
                      {overdueCount} overdue
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded hover:bg-[#122036] text-slate-400"
                data-testid="notification-close-btn"
              >
                <X size={14} />
              </button>
            </div>
            {loading && items.length === 0 && (
              <div className="px-4 py-10 text-center text-slate-500 text-sm">Loading reminders…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-4 py-10 text-center text-slate-500 text-sm">
                <CheckCircle2 size={24} className="mx-auto mb-2 text-emerald-500/70" />
                No upcoming appointments. <br />
                <span className="text-[11px] text-slate-600">You&apos;ll see reminders here 3 days before they&apos;re due.</span>
              </div>
            )}
            <div className="divide-y divide-[#1E304B]">
              {items.map((it) => (
                <ReminderRow key={it.record_id} item={it} onSnooze={snooze} onDismiss={dismiss} />
              ))}
            </div>
          </div>
        )}
      </div>

      {banner && (
        <ReminderBanner
          item={banner.first}
          moreCount={banner.moreCount}
          onClose={() => setBanner(null)}
          onOpen={() => { setOpen(true); setBanner(null); }}
        />
      )}
    </>
  );
}

function ReminderRow({ item, onSnooze, onDismiss }) {
  const cfg = URGENCY[item.urgency] || URGENCY.yellow;
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [customWhen, setCustomWhen] = useState("");

  const handleSnooze = async (mins) => {
    setSnoozeOpen(false);
    await onSnooze(item.record_id, inMinutes(mins));
  };
  const handleCustomSnooze = async () => {
    if (!customWhen) return;
    setSnoozeOpen(false);
    setCustomWhen("");
    await onSnooze(item.record_id, customWhen);
  };

  return (
    <div className={`px-4 py-3 ${cfg.bg} border-l-4 ${cfg.border} relative`} data-testid={`reminder-row-${item.record_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[9px] font-mono-data uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}55` }}
            >
              {cfg.label}
            </span>
            <span className="text-[10px] text-slate-400 font-mono-data">{relativeWhen(item.minutes_until)}</span>
          </div>
          <div className="font-display font-semibold text-slate-100 text-sm truncate" data-testid={`reminder-athlete-${item.record_id}`}>
            {item.sailor_name}
          </div>
          <div className="text-[11px] text-slate-300 flex items-center gap-1 mt-0.5">
            <Calendar size={11} className="text-cyan-300/80" />
            <span className="font-mono-data">{fmtWhen(item.next_appointment)}</span>
          </div>
          {(item.dr || item.hospital) && (
            <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
              <Stethoscope size={11} className="text-slate-500" />
              <span className="truncate">{item.dr || "—"}{item.hospital ? ` · ${item.hospital}` : ""}</span>
            </div>
          )}
          {item.next_appointment_notes && (
            <div className="text-[11px] text-slate-300/90 mt-1 italic">&ldquo;{item.next_appointment_notes}&rdquo;</div>
          )}
          {item.advice && !item.next_appointment_notes && (
            <div className="text-[10px] text-slate-500 mt-1 line-clamp-2">{item.advice}</div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-1.5 mt-2 relative">
        <button
          onClick={() => setSnoozeOpen((v) => !v)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-slate-700 text-slate-300 hover:text-cyan-300 hover:border-cyan-500/40"
          data-testid={`reminder-snooze-${item.record_id}`}
        >
          <AlarmClock size={11} /> Snooze
        </button>
        <button
          onClick={() => onDismiss(item.record_id)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-slate-700 text-slate-300 hover:text-emerald-300 hover:border-emerald-500/40"
          data-testid={`reminder-dismiss-${item.record_id}`}
        >
          <CheckCircle2 size={11} /> Dismiss
        </button>

        {snoozeOpen && (
          <div className="absolute top-full right-0 mt-1 w-56 rounded-md border border-[#1E304B] bg-[#0E1A2E] shadow-xl p-2 z-20" data-testid={`snooze-popover-${item.record_id}`}>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 px-1 pb-1">Snooze for</div>
            {[
              { label: "10 minutes", mins: 10 },
              { label: "1 hour",     mins: 60 },
              { label: "1 day",      mins: 60 * 24 },
            ].map((o) => (
              <button
                key={o.mins}
                onClick={() => handleSnooze(o.mins)}
                className="w-full text-left px-2 py-1.5 rounded text-[11px] text-slate-200 hover:bg-cyan-500/15 hover:text-cyan-200 flex items-center gap-2"
                data-testid={`snooze-${o.mins}m`}
              >
                <Clock size={11} className="text-slate-500" /> {o.label}
              </button>
            ))}
            <div className="border-t border-[#1E304B] mt-1 pt-1.5">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 px-1 pb-1">Custom</div>
              <input
                type="datetime-local"
                value={customWhen}
                onChange={(e) => setCustomWhen(e.target.value)}
                className="form-input text-[11px]"
                data-testid={`snooze-custom-input-${item.record_id}`}
              />
              <button
                onClick={handleCustomSnooze}
                disabled={!customWhen}
                className="w-full mt-1.5 px-2 py-1.5 rounded text-[11px] bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 disabled:opacity-40"
                data-testid={`snooze-custom-apply-${item.record_id}`}
              >
                Snooze until that time
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReminderBanner({ item, moreCount, onOpen, onClose }) {
  const cfg = URGENCY[item.urgency] || URGENCY.orange;
  return (
    <div
      className={`fixed top-20 right-4 z-50 w-[360px] rounded-xl border ${cfg.border} ${cfg.bg} backdrop-blur-md shadow-2xl p-4 fade-up`}
      data-testid="reminder-login-banner"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <BellRing size={18} style={{ color: cfg.color }} className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-mono-data" style={{ color: cfg.color }}>
              {item.urgency === "red" ? "Overdue appointment" : "Appointment due soon"}
            </div>
            <div className="font-display font-semibold text-slate-100 text-sm mt-0.5 truncate">{item.sailor_name}</div>
            <div className="text-[11px] text-slate-300 font-mono-data">{fmtWhen(item.next_appointment)}</div>
            {moreCount > 0 && (
              <div className="text-[10px] text-slate-400 mt-1">+ {moreCount} more pending reminder{moreCount === 1 ? "" : "s"}</div>
            )}
          </div>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-black/30 text-slate-400" data-testid="banner-close-btn">
          <X size={14} />
        </button>
      </div>
      <div className="flex items-center justify-end gap-1.5 mt-3">
        <button
          onClick={onClose}
          className="px-2 py-1 rounded text-[11px] border border-slate-700 text-slate-300 hover:text-slate-100"
          data-testid="banner-later-btn"
        >
          Later
        </button>
        <button
          onClick={onOpen}
          className="px-2 py-1 rounded text-[11px] bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 hover:bg-cyan-500/30"
          data-testid="banner-review-btn"
        >
          Review reminders
        </button>
      </div>
    </div>
  );
}
